import { FC, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { PreviewVariablePackage, Variable } from '@mobileTypes'

interface PreviewVariablesSettingsDialogProps {
  open: boolean
  localVariables: Variable[]
  globalVariables: Variable[]
  localPackages: PreviewVariablePackage[]
  globalPackages: PreviewVariablePackage[]
  selectedLocalPackageId?: string
  selectedGlobalPackageId?: string
  onLocalPackagesChange: (packages: PreviewVariablePackage[]) => void
  onGlobalPackagesChange: (packages: PreviewVariablePackage[]) => void
  onSelectedLocalPackageChange: (packageId?: string) => void
  onSelectedGlobalPackageChange: (packageId?: string) => void
  onClose: () => void
  onApply: () => void
}

type VariablePackagePanelProps = {
  title: string
  description: string
  variables: Variable[]
  packages: PreviewVariablePackage[]
  selectedPackageId?: string
  onPackagesChange: (packages: PreviewVariablePackage[]) => void
  onSelectedPackageChange: (packageId?: string) => void
}

const buildDefaultValues = (variables: Variable[]): Record<string, any> => {
  return variables.reduce((acc, variable) => {
    acc[variable.internalId] = variable.defaultValue ?? ''

    return acc
  }, {} as Record<string, any>)
}

const createPackage = (name: string, variables: Variable[]): PreviewVariablePackage => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name,
  values: buildDefaultValues(variables),
})

const normalizeImportedPackage = (value: unknown, variables: Variable[]): PreviewVariablePackage | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const source = value as Partial<PreviewVariablePackage>
  const values = source.values && typeof source.values === 'object' ? source.values : null

  if (!values) {
    return null
  }

  return {
    id: typeof source.id === 'string' ? source.id : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name: typeof source.name === 'string' && source.name.trim() ? source.name.trim() : 'Imported package',
    values: {
      ...buildDefaultValues(variables),
      ...values,
    },
  }
}

const formatValueForInput = (value: any, variable: Variable): string => {
  if (value === undefined || value === null) {
    return ''
  }

  if (variable.variableType === 'multiple' || typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }

  return String(value)
}

const parseValueFromInput = (value: string, variable: Variable): any => {
  if (variable.variableType === 'multiple' || variable.valueType === 'object' || variable.valueType === 'json') {
    try {
      return value.trim() ? JSON.parse(value) : variable.variableType === 'multiple' ? [] : null
    } catch {
      return value
    }
  }

  if (variable.valueType === 'number' || variable.valueType === 'integer') {
    return value.trim() === '' ? '' : Number(value)
  }

  return value
}

const VariablePackagePanel: FC<VariablePackagePanelProps> = ({
  title,
  description,
  variables,
  packages,
  selectedPackageId,
  onPackagesChange,
  onSelectedPackageChange,
}) => {
  const [configText, setConfigText] = useState('')
  const [importError, setImportError] = useState<string | null>(null)

  const selectedPackage = useMemo(
    () => packages.find(pkg => pkg.id === selectedPackageId),
    [packages, selectedPackageId],
  )

  const handleCreatePackage = () => {
    const pkg = createPackage(`${title} Package ${packages.length + 1}`, variables)
    onPackagesChange([...packages, pkg])
    onSelectedPackageChange(pkg.id)
    setConfigText('')
    setImportError(null)
  }

  const handleDeletePackage = () => {
    if (!selectedPackage) {
      return
    }

    const nextPackages = packages.filter(pkg => pkg.id !== selectedPackage.id)
    onPackagesChange(nextPackages)
    onSelectedPackageChange(nextPackages[0]?.id)
    setConfigText('')
    setImportError(null)
  }

  const handlePackageNameChange = (name: string) => {
    if (!selectedPackage) {
      return
    }

    onPackagesChange(
      packages.map(pkg => (pkg.id === selectedPackage.id ? { ...pkg, name } : pkg)),
    )
  }

  const handleVariableValueChange = (variable: Variable, value: any) => {
    if (!selectedPackage) {
      return
    }

    onPackagesChange(
      packages.map(pkg =>
        pkg.id === selectedPackage.id
          ? {
              ...pkg,
              values: {
                ...pkg.values,
                [variable.internalId]: value,
              },
            }
          : pkg,
      ),
    )
  }

  const handleExportPackage = () => {
    if (!selectedPackage) {
      return
    }

    setConfigText(JSON.stringify(selectedPackage, null, 2))
    setImportError(null)
  }

  const handleImportPackage = () => {
    try {
      const parsed = JSON.parse(configText)
      const importedPackage = normalizeImportedPackage(parsed, variables)

      if (!importedPackage) {
        setImportError('Config must contain a values object')

        return
      }

      const nextPackages = packages.filter(pkg => pkg.id !== importedPackage.id)
      onPackagesChange([...nextPackages, importedPackage])
      onSelectedPackageChange(importedPackage.id)
      setImportError(null)
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Invalid JSON')
    }
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant='subtitle1'>{title}</Typography>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 2 }}>
        {description}
      </Typography>

      <Stack spacing={2}>
        <FormControl fullWidth size='small'>
          <InputLabel>Package</InputLabel>
          <Select
            value={selectedPackageId ?? ''}
            label='Package'
            onChange={event => onSelectedPackageChange(event.target.value || undefined)}
          >
            {packages.length === 0 ? (
              <MenuItem value=''>No packages</MenuItem>
            ) : (
              packages.map(pkg => (
                <MenuItem key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Stack direction='row' spacing={1}>
          <Button size='small' variant='outlined' onClick={handleCreatePackage}>
            Create Package
          </Button>
          <Button size='small' variant='outlined' onClick={handleDeletePackage} disabled={!selectedPackage}>
            Delete
          </Button>
        </Stack>

        {selectedPackage && (
          <>
            <TextField
              size='small'
              label='Package Name'
              value={selectedPackage.name}
              onChange={event => handlePackageNameChange(event.target.value)}
              fullWidth
            />

            <Divider />

            {variables.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No variables configured.
              </Typography>
            ) : (
              variables.map(variable =>
                variable.valueType === 'boolean' ? (
                  <FormControlLabel
                    key={variable.internalId}
                    control={
                      <Checkbox
                        checked={Boolean(selectedPackage.values[variable.internalId])}
                        onChange={event =>
                          handleVariableValueChange(variable, event.target.checked)
                        }
                      />
                    }
                    label={`${variable.internalId} (${variable.valueType})`}
                  />
                ) : (
                  <TextField
                    key={variable.internalId}
                    size='small'
                    label={`${variable.internalId} (${variable.valueType})`}
                    value={formatValueForInput(selectedPackage.values[variable.internalId], variable)}
                    onChange={event =>
                      handleVariableValueChange(
                        variable,
                        parseValueFromInput(event.target.value, variable),
                      )
                    }
                    fullWidth
                    multiline={variable.variableType === 'multiple' || typeof selectedPackage.values[variable.internalId] === 'object'}
                    minRows={variable.variableType === 'multiple' ? 3 : 1}
                  />
                ),
              )
            )}

            <Divider />

            <Stack direction='row' spacing={1}>
              <Button size='small' variant='outlined' onClick={handleExportPackage}>
                Export
              </Button>
              <Button size='small' variant='outlined' onClick={handleImportPackage}>
                Import
              </Button>
            </Stack>
            <TextField
              label='Package Config'
              value={configText}
              onChange={event => setConfigText(event.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder='Paste package JSON here or click Export to copy this package config'
            />
            {importError && <Alert severity='error'>{importError}</Alert>}
          </>
        )}
      </Stack>
    </Box>
  )
}

export const PreviewVariablesSettingsDialog: FC<PreviewVariablesSettingsDialogProps> = ({
  open,
  localVariables,
  globalVariables,
  localPackages,
  globalPackages,
  selectedLocalPackageId,
  selectedGlobalPackageId,
  onLocalPackagesChange,
  onGlobalPackagesChange,
  onSelectedLocalPackageChange,
  onSelectedGlobalPackageChange,
  onClose,
  onApply,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>Manage Variables</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <VariablePackagePanel
            title='Local'
            description='Available only for this list item configurator.'
            variables={localVariables}
            packages={localPackages}
            selectedPackageId={selectedLocalPackageId}
            onPackagesChange={onLocalPackagesChange}
            onSelectedPackageChange={onSelectedLocalPackageChange}
          />

          <Divider orientation='vertical' flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          <VariablePackagePanel
            title='Global'
            description='Shared across all list item configurators.'
            variables={globalVariables}
            packages={globalPackages}
            selectedPackageId={selectedGlobalPackageId}
            onPackagesChange={onGlobalPackagesChange}
            onSelectedPackageChange={onSelectedGlobalPackageChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onApply} variant='contained'>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
